import { CapacitorHttp } from '@capacitor/core'
import { isPlatform } from '@ionic/vue'
import { sha256 } from 'js-sha256'
import { v4 as uuid } from 'uuid'
import { useTrackStore } from '~/store'
import {
  TRACKING_MESSAGE_KEY,
  TRACKING_PROTOCOL_VERSION,
  isTrackingBridgeMessage,
  type CanonicalActionSource,
  type CanonicalEntryMode,
  type CanonicalTrackingEvent,
  type CanonicalTrackingItem,
  type TrackingAckMessage,
  type TrackingBridgeMessage,
  type TrackingEventMessage,
  type TrackingReadyMessage
} from '~/types/canonicalTracking'

const ACK_TIMEOUT_MS = 1500
const HANDSHAKE_RETRY_MS = 500
const MAX_HANDSHAKE_ATTEMPTS = 3
const MAX_SEND_ATTEMPTS = 3

type BridgeStatus = 'initialising' | 'embedded' | 'standalone' | 'native'

interface PendingEvent {
  event: CanonicalTrackingEvent
  attempts: number
  timer?: ReturnType<typeof setTimeout>
}

let bridgeStatus: BridgeStatus = isPlatform('capacitor') ? 'native' : 'initialising'
let journeyId: string | undefined
let handshakeTimer: ReturnType<typeof setTimeout> | undefined
let readyMessageId: string | undefined
let isInitialised = false
const queuedEvents: CanonicalTrackingEvent[] = []
const pendingEvents = new Map<string, PendingEvent>()

export function isCanonicalTrackingEnabled(): boolean {
  return import.meta.env.VITE_SGTM_ENABLED === 'true' && Boolean(import.meta.env.VITE_SGTM_ENDPOINT_URL)
}

function configuredParentOrigin(): string | undefined {
  const configuredUrl = import.meta.env.VITE_BASE_MARKETING_SITE_URL
  if (!configuredUrl) return undefined

  try {
    return new URL(configuredUrl).origin
  } catch {
    console.warn('Invalid VITE_BASE_MARKETING_SITE_URL; parent messaging is disabled')
    return undefined
  }
}

function parseMessage(data: unknown): unknown {
  if (typeof data !== 'string') return data

  try {
    return JSON.parse(data)
  } catch {
    return undefined
  }
}

function postToParent(message: TrackingBridgeMessage): boolean {
  const targetOrigin = configuredParentOrigin()
  if (!targetOrigin || window.parent === window) return false

  window.parent.postMessage(JSON.stringify(message), targetOrigin)
  return true
}

function handleParentMessage(event: MessageEvent): void {
  const parentOrigin = configuredParentOrigin()
  if (!parentOrigin || event.origin !== parentOrigin || event.source !== window.parent) return

  const message = parseMessage(event.data)
  if (!isTrackingBridgeMessage(message)) return

  if (message.type === 'init' && bridgeStatus === 'initialising' && message.message_id === readyMessageId) {
    if (handshakeTimer) clearTimeout(handshakeTimer)
    journeyId = message.journey_id
    bridgeStatus = 'embedded'
    flushQueuedEvents()
    return
  }

  if (message.type === 'ack') {
    const pending = pendingEvents.get(message.event_id)
    if (pending?.timer) clearTimeout(pending.timer)
    pendingEvents.delete(message.event_id)
  }
}

function enterStandaloneMode(): void {
  if (bridgeStatus !== 'initialising') return
  bridgeStatus = 'standalone'
  console.warn('Canonical tracking bridge handshake was not completed; using standalone delivery', {
    bridgeMode: bridgeStatus,
    configuredParentOrigin: Boolean(configuredParentOrigin())
  })
  flushQueuedEvents()
}

export function initialiseCanonicalTracking(): void {
  if (!isCanonicalTrackingEnabled() || isInitialised) return
  isInitialised = true

  if (isPlatform('capacitor')) {
    bridgeStatus = 'native'
    return
  }

  if (window.parent === window || !configuredParentOrigin()) {
    bridgeStatus = 'standalone'
    return
  }

  window.addEventListener('message', handleParentMessage)
  readyMessageId = uuid()
  sendReadyMessage(1)
}

function sendReadyMessage(attempt: number): void {
  const readyMessage: TrackingReadyMessage = {
    key: TRACKING_MESSAGE_KEY,
    version: TRACKING_PROTOCOL_VERSION,
    type: 'ready',
    message_id: readyMessageId!
  }
  postToParent(readyMessage)
  handshakeTimer = setTimeout(() => {
    if (bridgeStatus !== 'initialising') return
    if (attempt >= MAX_HANDSHAKE_ATTEMPTS) {
      enterStandaloneMode()
      return
    }
    sendReadyMessage(attempt + 1)
  }, HANDSHAKE_RETRY_MS)
}

function flushQueuedEvents(): void {
  const events = queuedEvents.splice(0)
  for (const event of events) void deliverEvent(event)
}

function retryEmbeddedEvent(eventId: string): void {
  const pending = pendingEvents.get(eventId)
  if (!pending) return

  if (pending.attempts >= MAX_SEND_ATTEMPTS) {
    pendingEvents.delete(eventId)
    console.warn(`Tracking event ${eventId} was not acknowledged by the parent`)
    return
  }

  pending.attempts += 1
  postEmbeddedEvent(pending)
}

function postEmbeddedEvent(pending: PendingEvent): void {
  const message: TrackingEventMessage = {
    key: TRACKING_MESSAGE_KEY,
    version: TRACKING_PROTOCOL_VERSION,
    type: 'event',
    event: {
      ...pending.event,
      entry_mode: 'embedded',
      journey_id: journeyId
    }
  }
  postToParent(message)
  pending.timer = setTimeout(() => retryEmbeddedEvent(pending.event.event_id), ACK_TIMEOUT_MS)
}

async function sendDirect(event: CanonicalTrackingEvent): Promise<void> {
  const endpoint = new URL(import.meta.env.VITE_SGTM_EVENT_PATH || '/events', import.meta.env.VITE_SGTM_ENDPOINT_URL)

  if (isPlatform('capacitor')) {
    const response = await CapacitorHttp.post({
      url: endpoint.toString(),
      headers: { 'Content-Type': 'application/json' },
      data: event,
      connectTimeout: 5000,
      readTimeout: 10000
    })

    if (response.status < 200 || response.status >= 300) {
      throw new Error(`sGTM ingestion returned HTTP ${response.status}`)
    }
    return
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(event),
    keepalive: true
  })

  if (!response.ok) {
    throw new Error(`sGTM ingestion returned HTTP ${response.status}`)
  }
}

async function deliverEvent(event: CanonicalTrackingEvent): Promise<void> {
  if (bridgeStatus === 'initialising') {
    queuedEvents.push(event)
    return
  }

  if (bridgeStatus === 'embedded') {
    const pending: PendingEvent = { event, attempts: 1 }
    pendingEvents.set(event.event_id, pending)
    postEmbeddedEvent(pending)
    return
  }

  try {
    await sendDirect({
      ...event,
      entry_mode: bridgeStatus === 'native' ? 'native' : 'standalone'
    })
  } catch (error) {
    console.warn(`Error sending canonical tracking event ${event.event_name}`, error)
  }
}

function getActionSource(): CanonicalActionSource {
  if (isPlatform('ios') && isPlatform('capacitor')) return 'app_ios'
  if (isPlatform('android') && isPlatform('capacitor')) return 'app_android'
  return 'web'
}

function getEntryMode(): CanonicalEntryMode {
  if (bridgeStatus === 'native') return 'native'
  if (bridgeStatus === 'embedded') return 'embedded'
  return 'standalone'
}

function normaliseItems(value: unknown): CanonicalTrackingItem[] | undefined {
  if (!Array.isArray(value)) return undefined

  return value
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .map((item) => ({
      item_id: String(item.item_id ?? item.id ?? ''),
      ...(item.item_name || item.name ? { item_name: String(item.item_name ?? item.name) } : {}),
      ...(typeof item.price === 'number' ? { price: item.price } : {}),
      ...(typeof item.quantity === 'number' ? { quantity: item.quantity } : {}),
      ...(item.item_category ? { item_category: String(item.item_category) } : {})
    }))
    .filter((item) => item.item_id)
}

function getHashedUserData(): CanonicalTrackingEvent['user_data'] {
  const trackStore = useTrackStore()
  const normalisedEmail = trackStore.user?.email?.trim().toLowerCase()
  const normalisedPhone = trackStore.user?.phone?.replace(/\D/g, '').replace(/^0+/, '')
  const advertisingId =
    trackStore.advertisingIdentifiers?.attAuthorizationStatus === 'Authorized'
      ? trackStore.advertisingIdentifiers.advertiserId
      : undefined

  const userData = {
    ...(normalisedEmail ? { email_sha256: sha256(normalisedEmail) } : {}),
    ...(normalisedPhone ? { phone_sha256: sha256(`61${normalisedPhone}`) } : {}),
    ...(trackStore.user?.externalId ? { external_id_sha256: sha256(trackStore.user.externalId) } : {}),
    ...(advertisingId ? { advertising_id: advertisingId } : {})
  }

  return Object.keys(userData).length ? userData : undefined
}

export function createCanonicalTrackingEvent(
  eventName: string,
  params: Record<string, unknown> = {},
  eventId?: string
): CanonicalTrackingEvent {
  const environment = import.meta.env.VITE_DEPLOY_ENV || 'unknown'
  const orderId = params.transaction_id ?? params.order_id ?? params.redcatOrderId
  const trackStore = useTrackStore()
  const attribution = trackStore.getActiveAttribution()

  return {
    event_name: eventName,
    event_id: eventId ?? createCanonicalEventId(eventName, environment, orderId),
    event_time: Date.now(),
    environment,
    action_source: getActionSource(),
    entry_mode: getEntryMode(),
    ...(typeof window !== 'undefined' && getActionSource() === 'web'
      ? {
          page_url: window.location.href,
          referrer: document.referrer || undefined
        }
      : {}),
    ...(typeof params.value === 'number' ? { value: params.value } : {}),
    ...(typeof params.currency === 'string' ? { currency: params.currency } : {}),
    ...(orderId ? { order_id: String(orderId) } : {}),
    ...(normaliseItems(params.items ?? params.contents)
      ? { items: normaliseItems(params.items ?? params.contents) }
      : {}),
    ...(attribution
      ? {
          attribution: {
            source: attribution.source,
            medium: attribution.medium,
            campaign: attribution.campaign,
            content: attribution.content,
            term: attribution.term,
            meta_click_id: attribution.metaClickId,
            pinterest_click_id: attribution.pinterestClickId,
            reddit_click_id: attribution.redditClickId,
            snapchat_click_id: attribution.snapchatClickId,
            tiktok_click_id: attribution.tikTokClickId,
            captured_at: attribution.capturedAt,
            last_activity_at: attribution.lastActivityAt
          }
        }
      : {}),
    ...(getHashedUserData() ? { user_data: getHashedUserData() } : {}),
    consent: {
      analytics: 'not_implemented',
      advertising: 'not_implemented',
      ...(trackStore.advertisingIdentifiers?.attAuthorizationStatus
        ? { att_status: trackStore.advertisingIdentifiers.attAuthorizationStatus }
        : {})
    },
    parameters: params
  }
}

export function createCanonicalEventId(
  eventName: string,
  environment: string,
  orderId?: unknown,
  randomId: () => string = uuid
): string {
  return eventName === 'purchase' && orderId
    ? `${environment}:purchase:${String(orderId)}`
    : `${environment}:${eventName}:${randomId()}`
}

export function createClientTrackingEventId(eventName: string, orderId?: unknown): string {
  return createCanonicalEventId(eventName, import.meta.env.VITE_DEPLOY_ENV || 'unknown', orderId)
}

export function trackCanonicalEvent(
  eventName: string,
  params: Record<string, unknown> = {},
  eventId?: string
): string | undefined {
  if (!isCanonicalTrackingEnabled()) return
  initialiseCanonicalTracking()
  const event = createCanonicalTrackingEvent(eventName, params, eventId)
  if (eventName.toLowerCase() === 'purchase') {
    useTrackStore().clearAttribution('purchase')
  }
  void deliverEvent(event)
  return event.event_id
}

export function acknowledgeTrackingEvent(eventId: string): TrackingAckMessage {
  return {
    key: TRACKING_MESSAGE_KEY,
    version: TRACKING_PROTOCOL_VERSION,
    type: 'ack',
    event_id: eventId
  }
}
